import { startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  OrganizationMemberRoleName,
  ReportMessageListResponse,
  ReportMessageOptionsResponse,
  ReportMessagePageSize,
  ReportMessageSortName,
  ReportMessageStatusBucketName,
  ReportMessageSummaryResponse,
  MessageOriginName,
  MessageStatusName,
  PlatformTypeName,
} from "@multi-chat/shared";
import {
  getReportMessagesOptions,
  getReportMessagesSummary,
  listReportMessages,
} from "../../api/reports-api.js";
import {
  clearOutgoingFilters,
  hasActiveOutgoingFilters,
  outgoingReportHref,
  parseOutgoingReportQuery,
  type OutgoingReportQuery,
} from "./outgoing-report-query.js";

const SEARCH_DEBOUNCE_MS = 280;

export function useOutgoingMessagesReport(input: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): {
  query: OutgoingReportQuery;
  debouncedSearch: string;
  list: ReportMessageListResponse | null;
  summary: ReportMessageSummaryResponse | null;
  options: ReportMessageOptionsResponse | null;
  loading: boolean;
  error: unknown;
  filtersActive: boolean;
  canAccess: boolean;
  dateFrom: string;
  dateTo: string;
  setSearch: (search: string) => void;
  setDateRange: (from: string, to: string) => void;
  setPlatform: (platform: PlatformTypeName | undefined) => void;
  setOrigin: (origin: MessageOriginName | undefined) => void;
  setStatusBucket: (statusBucket: ReportMessageStatusBucketName | null) => void;
  setStatus: (status: MessageStatusName | undefined) => void;
  setEndpointId: (endpointId: number | undefined) => void;
  setCampaignId: (campaignId: number | undefined) => void;
  setTemplate: (template: string | undefined) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: ReportMessagePageSize) => void;
  setSort: (sort: ReportMessageSortName) => void;
  resetFilters: () => void;
  refresh: () => void;
} {
  const canAccess = input.role === "OWNER" || input.role === "ADMIN";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = useMemo(
    () => parseOutgoingReportQuery(searchParams.toString()),
    [searchParams],
  );

  const [searchDraft, setSearchDraft] = useState(query.search);
  const [debouncedSearch, setDebouncedSearch] = useState(query.search);
  const [list, setList] = useState<ReportMessageListResponse | null>(null);
  const [summary, setSummary] = useState<ReportMessageSummaryResponse | null>(null);
  const [options, setOptions] = useState<ReportMessageOptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setSearchDraft(query.search);
    setDebouncedSearch(query.search);
  }, [query.search]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchDraft.trim();
      setDebouncedSearch(next);
      if (next === query.search) return;
      navigate(
        outgoingReportHref({ ...query, search: next, page: 1 }),
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchDraft, query, navigate]);

  const applyQuery = useCallback(
    (next: OutgoingReportQuery, options?: { replace?: boolean }) => {
      navigate(outgoingReportHref(next), { replace: options?.replace !== false });
    },
    [navigate],
  );

  const patchQuery = useCallback(
    (
      patch: Partial<OutgoingReportQuery> & {
        clearStatusBucket?: boolean;
        clearStatus?: boolean;
        clearDates?: boolean;
      },
      resetPage = true,
    ) => {
      const {
        clearStatusBucket,
        clearStatus,
        clearDates,
        ...rest
      } = patch;
      const next: OutgoingReportQuery = {
        ...query,
        ...rest,
        page: resetPage ? 1 : (rest.page ?? query.page),
      };
      if (clearStatusBucket) delete next.statusBucket;
      if (clearStatus) delete next.status;
      if (clearDates) {
        delete next.from;
        delete next.to;
      }
      if (rest.statusBucket !== undefined) {
        delete next.status;
      }
      if (rest.status !== undefined) {
        delete next.statusBucket;
      }
      applyQuery(next);
    },
    [applyQuery, query],
  );

  const load = useEffectEvent(async (signal: AbortSignal) => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    const sharedFilters = {
      ...(query.from && query.to
        ? { fromDate: query.from, toDate: query.to }
        : {}),
      platform: query.platform,
      origin: query.origin,
      endpointId: query.endpointId,
      campaignId: query.campaignId,
      template: query.template,
      search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
    };
    try {
      const [nextList, nextSummary, nextOptions] = await Promise.all([
        listReportMessages(
          input.organizationId,
          {
            ...sharedFilters,
            statusBucket: query.statusBucket,
            status: query.statusBucket ? undefined : query.status,
            page: query.page,
            pageSize: query.pageSize,
            sort: query.sort,
          },
          signal,
        ),
        getReportMessagesSummary(input.organizationId, sharedFilters, signal),
        getReportMessagesOptions(input.organizationId, {}, signal),
      ]);
      if (signal.aborted) return;
      startTransition(() => {
        setList(nextList);
        setSummary(nextSummary);
        setOptions(nextOptions);
      });
    } catch (caught) {
      if (signal.aborted) return;
      setError(caught);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  });

  useEffect(() => {
    if (!canAccess) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [
    canAccess,
    input.organizationId,
    query.from,
    query.to,
    query.platform,
    query.origin,
    query.statusBucket,
    query.status,
    query.endpointId,
    query.campaignId,
    query.template,
    query.page,
    query.pageSize,
    query.sort,
    debouncedSearch,
    refreshToken,
  ]);

  return {
    query: { ...query, search: searchDraft },
    debouncedSearch,
    list,
    summary,
    options,
    loading,
    error,
    filtersActive: hasActiveOutgoingFilters({ ...query, search: searchDraft }),
    canAccess,
    /** Organization-calendar days for date inputs (URL explicit or API-resolved default). */
    dateFrom: query.from ?? summary?.fromDate ?? "",
    dateTo: query.to ?? summary?.toDate ?? "",
    setSearch: setSearchDraft,
    setDateRange: (from, to) => {
      if (!from || !to) {
        patchQuery({ clearDates: true });
        return;
      }
      patchQuery({ from, to });
    },
    setPlatform: (platform) =>
      patchQuery(platform ? { platform } : { platform: undefined }),
    setOrigin: (origin) => patchQuery(origin ? { origin } : { origin: undefined }),
    setStatusBucket: (statusBucket) => {
      if (statusBucket == null) {
        patchQuery({ clearStatusBucket: true, clearStatus: true });
        return;
      }
      patchQuery({ statusBucket, clearStatus: true });
    },
    setStatus: (status) => {
      if (status == null) {
        patchQuery({ clearStatus: true });
        return;
      }
      patchQuery({ status, clearStatusBucket: true });
    },
    setEndpointId: (endpointId) =>
      patchQuery(endpointId != null ? { endpointId } : { endpointId: undefined }),
    setCampaignId: (campaignId) =>
      patchQuery(campaignId != null ? { campaignId } : { campaignId: undefined }),
    setTemplate: (template) =>
      patchQuery(template?.trim() ? { template: template.trim() } : { template: undefined }),
    setPage: (page) => patchQuery({ page }, false),
    setPageSize: (pageSize) => patchQuery({ pageSize }),
    setSort: (sort) => patchQuery({ sort }),
    resetFilters: () => applyQuery(clearOutgoingFilters(query)),
    refresh: () => setRefreshToken((token) => token + 1),
  };
}
